/**
 * Utilities to extract field values from validation data
 */

import { ValidationPageData } from '../services/types';

/**
 * Extract country value from validation data.
 * Searches all tables for a row with field_name or extraction_key containing "country".
 *
 * @param validationData - Validation page data containing tables with extracted fields
 * @param overrides - User overrides for field values, keyed by tableKey and then extractionKey
 * @returns Country value if found, undefined otherwise
 */
export function extractCountryFromValidation(
  validationData: ValidationPageData | null,
  overrides: Record<string, Record<string, string>>
): string | undefined {
  if (!validationData) return undefined;

  // Search through all tables
  for (const tableKey of Object.keys(validationData.tables)) {
    const table = validationData.tables[tableKey];
    if (!table) continue;

    // Look for a row with "country" in the field name
    const countryRow = table.rows.find(row =>
      row.field_name.toLowerCase().includes('country') ||
      row.extraction_key.toLowerCase().includes('country')
    );

    if (countryRow) {
      // Use override if available, otherwise use extracted value
      const overrideValue = overrides[tableKey]?.[countryRow.extraction_key];
      if (overrideValue !== undefined) {
        // Override exists - return it as-is (even if empty string)
        return overrideValue;
      }
      // No override - use extracted value, return undefined if empty
      const value = countryRow.extracted_value || undefined;
      return value && value.trim() !== '' ? value : undefined;
    }
  }

  return undefined;
}

/**
 * Extract product name from validation data.
 * Searches all tables for a row with field_name or extraction_key containing "product".
 *
 * @param validationData - Validation page data containing tables with extracted fields
 * @param overrides - User overrides for field values, keyed by tableKey and then extractionKey
 * @returns Product name if found, undefined otherwise
 */
export function extractProductNameFromValidation(
  validationData: ValidationPageData | null,
  overrides: Record<string, Record<string, string>>
): string | undefined {
  if (!validationData) return undefined;

  // Search through all tables
  for (const tableKey of Object.keys(validationData.tables)) {
    const table = validationData.tables[tableKey];
    if (!table) continue;

    // Look for a row with "product" in the field name (but not "by-product" or similar)
    const productRow = table.rows.find(row => {
      const fieldLower = row.field_name.toLowerCase();
      const keyLower = row.extraction_key.toLowerCase();
      return (fieldLower.includes('product') && !fieldLower.includes('by-product')) ||
             (keyLower.includes('product') && !keyLower.includes('by-product'));
    });

    if (productRow) {
      // Use override if available, otherwise use extracted value
      const overrideValue = overrides[tableKey]?.[productRow.extraction_key];
      if (overrideValue !== undefined) {
        // Override exists - return it as-is (even if empty string)
        return overrideValue;
      }
      // No override - use extracted value, return undefined if empty
      const value = productRow.extracted_value || undefined;
      return value && value.trim() !== '' ? value : undefined;
    }
  }

  return undefined;
}

/**
 * Extract inspection lot ID from validation data.
 * Searches all tables for a row with field_name or extraction_key containing "batch" or "lot".
 *
 * @param validationData - Validation page data containing tables with extracted fields
 * @param overrides - User overrides for field values, keyed by tableKey and then extractionKey
 * @returns Inspection lot ID if found, undefined otherwise
 */
export function extractInspectionLotFromValidation(
  validationData: ValidationPageData | null,
  overrides: Record<string, Record<string, string>>
): string | undefined {
  if (!validationData) return undefined;

  // Search through all tables
  for (const tableKey of Object.keys(validationData.tables)) {
    const table = validationData.tables[tableKey];
    if (!table) continue;

    // Look for a row specifically for inspection lot - NOT generic "batch" fields
    // This avoids matching CMO Batch ID, FP Batch ID, etc. which are NOT inspection lot IDs
    const lotRow = table.rows.find(row => {
      const fieldLower = row.field_name.toLowerCase();
      const keyLower = row.extraction_key.toLowerCase();

      // Match fields that contain "inspection" AND "lot" together (e.g., "Inspection Lot", "Inspection Lot Number")
      const hasInspectionLot = (fieldLower.includes('inspection') && fieldLower.includes('lot')) ||
                               (keyLower.includes('inspection') && keyLower.includes('lot'));

      // Also match generic "Lot Number" or "Lot No" (but not just "lot" alone to avoid "Lot Quantity")
      const hasLotNumber = fieldLower.includes('lot number') ||
                           fieldLower.includes('lot no') ||
                           keyLower.includes('lot_number') ||
                           keyLower.includes('lot_no');

      return hasInspectionLot || hasLotNumber;
    });

    if (lotRow) {
      // Use override if available, otherwise use extracted value
      const overrideValue = overrides[tableKey]?.[lotRow.extraction_key];
      if (overrideValue !== undefined) {
        // Override exists - return it as-is (even if empty string)
        return overrideValue;
      }
      // No override - use extracted value, return undefined if empty
      const value = lotRow.extracted_value || undefined;
      return value && value.trim() !== '' ? value : undefined;
    }
  }

  return undefined;
}

/**
 * Extract validated manufacturing date from validation data and overrides.
 * Prioritizes user overrides over extracted values.
 * Returns date in DD.MM.YYYY format or null if not found.
 */
export function extractManufactureDate(
  validationData: any,
  overrides: Record<string, Record<string, any>>
): string | null {
  // Priority order: User overrides > COC > COA > DELIVERY_NOTE > BULK_COC > BULK_COA
  const sources = ['COC', 'COA', 'DELIVERY_NOTE', 'BULK_COC', 'BULK_COA'];

  for (const source of sources) {
    // Check overrides first
    if (overrides[source]?.manufacturing_date) {
      return overrides[source].manufacturing_date;
    }

    // Check extracted value from validation data tables
    const table = validationData?.tables?.[source];
    if (table?.rows) {
      const row = table.rows.find((r: any) => r.extraction_key === 'manufacturing_date');
      if (row?.extracted_value) {
        return row.extracted_value;
      }
    }
  }

  return null;
}

/**
 * Extract validated expiry date from validation data and overrides.
 * Prioritizes user overrides over extracted values.
 * Returns date in DD.MM.YYYY format or null if not found.
 */
export function extractExpiryDate(
  validationData: any,
  overrides: Record<string, Record<string, any>>
): string | null {
  // Priority order: User overrides > COC > COA > DELIVERY_NOTE > BULK_COC > BULK_COA
  const sources = ['COC', 'COA', 'DELIVERY_NOTE', 'BULK_COC', 'BULK_COA'];

  for (const source of sources) {
    // Check overrides first
    if (overrides[source]?.expiry_date) {
      return overrides[source].expiry_date;
    }

    // Check extracted value from validation data tables
    const table = validationData?.tables?.[source];
    if (table?.rows) {
      const row = table.rows.find((r: any) => r.extraction_key === 'expiry_date');
      if (row?.extracted_value) {
        return row.extracted_value;
      }
    }
  }

  return null;
}
